import React, { useCallback, useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { TableBody } from "@material-ui/core";
import { toast } from "react-toastify";
import ValidationLayout from "../AdminValidations";
import { AuthRoutes, SortOrder } from "../../enums";
import CollapsibleNe from "./Collapsible";
import GroupOperation from "./GroupOperations";
import AddPortService from "../../services/addPort.service";
import ManagePortsService from "../../services/managePorts.service";
import { TableConstants, HeadCellType, EditBandwidthFieldMapping } from "../../utils/appConstants";
import LoadingRow from "../DesignSystem/Table/Rows/LoadingRow";
import EmptyRow from "../DesignSystem/Table/Rows/EmptyRow";
import BandwidthTable from "./BandwidthTable";
import SaleDrawer from "./BandwidthTable/PutUpForSale";
import PdfViewer from "../PdfViewer";
import TableData from "./TableData";
import SearchIcon from "../../assets/images/search-icon.svg";
import {
  SuccessMessage,
  ErrorCodeMessageMapping,
  ErrorCodesMapping,
  MY_SELLER_BUCKET_PRE_URL,
  TERMS_DEFAULT_FILE,
  ErrorMessages as Errors,
  ErrorMessages,
} from "../../utils/apiConstants";
import ErrorInfoIcon from "../../assets/images/error-info-icon.svg";
import SuccessInfoIcon from "../../assets/images/success-toast-checkmark-icon.svg";
import EditBandwidthDrawer from "./EditBandwidth";
import ManageBandwidthService from "../../services/manageBandwidth.service";
import { sumCount } from "../../utils/methods";
import Button from "../DesignSystem/button";
import Input from "../DesignSystem/input";
import NodeView from "../../assets/images/node-view.svg";
import NodeViewSelected from "../../assets/images/node-view-selected.svg";
import ListView from "../../assets/images/list-icon.svg";
import ListViewSelected from "../../assets/images/list-icon-selected.svg";
import Dropdown from "../DesignSystem/dropdown_autocomplete";
import SwipeIcon from "../../assets/images/swipe-icon.svg";
import { useIsMounted } from "../../utils/customHooks";
import { usePutUpForSaleBandwidth } from "./BandwidthTable/PutUpForSale/usePutupForSaleBandwidth";
import { useGroup } from "../ManagePorts/GroupOperations/useGroup";

const ManageBandwidth: React.FC = () => {
  let rows: any;
  const { state: networkElementContext } = useLocation() as any;
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [order, setOrder] = useState<SortOrder>(SortOrder.asc);
  const [orderBy, setOrderBy] = useState<string>("farend_port_description");
  const [page, setPage] = useState<number>(1);
  const [searchTextNeElements, setSearchTextNeElements] = useState<string>(networkElementContext?.ne_name || "");
  const [searchTextFeElements, setSearchTextFeElements] = useState<string>("");
  const [neInput, setNeInput] = useState<string>("");
  const [feInput, setFeInput] = useState<string>("");

  const [bandwidthPorts, setBandwidthPorts] = useState<any[]>([]);
  const [selected, setSelected] = useState<any[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [isNodeView, setIsNodeView] = useState<boolean>(!!networkElementContext);
  const [searchText, setSearchText] = useState<string>("");
  const [prevSearchText, setPrevSearchText] = useState<string>("");
  const [networkElements, setNetworkElements] = useState<any[]>([]);
  const [allNetworkElements, setAllNetworkElements] = useState<any[]>([]);
  const [expandedNeId, setExpandedNeId] = useState<string>("");
  const [portsInNe, setPortsInNe] = useState<any[]>([]);
  const [groupsInNe, setGroupsInNe] = useState<any[]>([]);
  const [portsGroupsLoading, setPortsGroupsLoading] = useState<boolean>(false);
  const [selectedUnGroupId, setSelectedUnGroupId] = useState<string>("");
  const [groupedSelected, setGroupSelected] = useState<any[]>([]);
  const [unGroupedSelected, setUnGroupSelected] = useState<any[]>([]);
  const [expandedGroupIds, setExpandedGroupIds] = useState<string[]>([]);
  const [expandedOnNet, setExpandedOnNet] = useState<boolean>(false);
  const [editGroupOpen, setEditGroupOpen] = useState<boolean>(false);
  const [unGroupLoading, setUnGroupLoading] = useState<boolean>(false);
  const [groupLoading, setGroupLoading] = useState<boolean>(false);
  const [addGroupOpen, setAddGroupOpen] = useState<boolean>(false);
  const [ports, setPorts] = useState<any[]>([]);
  const [cancelSubLoading, setCancelSubLoading] = useState<string>("");
  const [editGroupDetails, setEditGroupDetails] = useState<{
    name: string;
    description: string;
    icon: string;
    id: string;
  }>({
    name: "",
    description: "",
    icon: "",
    id: "",
  });
  const isMounted = useIsMounted();
  const handlePageChange = useCallback(
    (page: number) => {
      setPage(page);
    },
    [setPage]
  );

  const handleRequestSort = useCallback(
    (property: string, nextSortOrder: SortOrder) => {
      setOrder(nextSortOrder);
      setOrderBy(property);
    },
    [orderBy, order]
  );
  const [seeMore, setSeeMore] = useState(false);

  const fetchNetworkElements = useCallback(
    (seeMore = false) => {
      AddPortService.getNetworkElements()
        .then((response: any) => {
          if (!isMounted) return;
          /*
          Displays all the network elements on which you have resources.
          If you have less than minNEs network resources, show all the resources
          Sort them by the number of resources and secondly by if they are on net or not
        */
          setAllNetworkElements(response.data.filter((ne: any) => sumCount(ne.bdCount) > 0));
          const minNEs = 10;
          const populatedNEs = response.data.filter((ne: any) => sumCount(ne.bdCount) > 0);
          const NEs = response.data && populatedNEs && populatedNEs.length >= minNEs && !seeMore ? populatedNEs : response.data || [];
          setSeeMore(seeMore || !(response.data && populatedNEs && populatedNEs.length >= minNEs && !seeMore));
          NEs.sort((a: any, b: any) => b.on_net - a.on_net);
          NEs.sort((a: any, b: any) => sumCount(b.bdCount) - sumCount(a.bdCount));
          if (searchTextNeElements) {
            NEs.sort((a: any, b: any) => b.ne_name === searchTextNeElements);
          }
          setNetworkElements(NEs);
        })
        .catch(() => {
          if (!isMounted) return;
          toast.error(Errors.internalServerError, { icon: <img src={ErrorInfoIcon} alt="Error" className="error-icon" /> });
          setNetworkElements([]);
        });
    },
    [setNetworkElements, seeMore, searchTextNeElements]
  );

  const fetchPortsAndGroups = useCallback(
    (expandedNE: string = "") => {
      const expandedNEtoFetch = expandedNE ? expandedNE : expandedNeId;
      if (expandedNEtoFetch) {
        setPortsGroupsLoading(true);
        ManageBandwidthService.getPortsAndGroups(expandedNEtoFetch, searchText)
          .then((response: any) => {
            if (!isMounted) return;
            setPortsInNe(response.data[0].ports || []);
            setGroupsInNe(response.data[0].groups || []);
            setExpandedGroupIds(response.data[0].groups && response.data[0].groups.map((group: any) => group.groupId));
            setPortsGroupsLoading(false);
          })
          .catch(() => {
            if (!isMounted) return;
            toast.error(Errors.internalServerError, { icon: <img src={ErrorInfoIcon} alt="Error" className="error-icon" /> });
            setPortsGroupsLoading(false);
            setPortsInNe([]);
            setGroupsInNe([]);
          });
      }
    },
    [expandedNeId, searchText, order, orderBy, setPortsGroupsLoading, setPortsInNe, setGroupsInNe]
  );

  const fetchBandwidthPorts = useCallback(() => {
    setLoading(true);
    const payload = {
      sortField: orderBy,
      sortDirection: order.toUpperCase(),
      page,
      pageSize: TableConstants.perPage,
      searchNneName: searchTextNeElements,
      searchFneName: searchTextFeElements,
      searchText,
    };

    ManageBandwidthService.getManageBandwidthPorts(payload)
      .then((response: any) => {
        if (!isMounted) return;
        setLoading(false);
        setBandwidthPorts(response?.data ?? []);
        setTotal(response?.pagination?.totalRecords ?? 0);
        setPrevSearchText(searchText);
      })
      .catch(() => {
        if (!isMounted) return;
        toast.error(Errors.internalServerError, { icon: <img src={ErrorInfoIcon} alt="Error" className="error-icon" /> });
        setLoading(false);
        setBandwidthPorts([]);
        setTotal(0);
      });
  }, [setLoading, setBandwidthPorts, setTotal, isMounted, total, orderBy, order, page, searchTextNeElements, searchTextFeElements, searchText]);

  const handlePortViewChange = useCallback(
    (checked: boolean) => {
      setIsNodeView(checked);
      setOrderBy("created_at");
      setPage(1);
      setPorts([]);
      setTotal(0);
      setSearchTextNeElements("");
      setSearchTextFeElements("");
    },
    [setIsNodeView, setOrderBy, setPage, setPorts, setTotal, setSearchTextNeElements, setSearchTextFeElements]
  );

  const handleCollapsibleNEClose = useCallback(() => {
    setExpandedNeId("");
    setExpandedOnNet(false);
  }, [setExpandedNeId, setExpandedOnNet]);

  const handleCollapsibleNEOpen = useCallback(
    (neId: string) => {
      setExpandedNeId(neId);
      fetchPortsAndGroups(neId);
      networkElements.forEach((ne: any) => {
        if (ne.id === neId) {
          setExpandedOnNet(ne.id);
        }
      });
      setGroupSelected([]);
      setUnGroupSelected([]);
      // setRequestVerificationSelected([[], []]);
      setSelectedUnGroupId("");
      setOrder(SortOrder.asc);
      setOrderBy("description");
      setExpandedGroupIds([]);
    },
    [networkElements, setExpandedOnNet, setExpandedNeId, setGroupSelected, setUnGroupSelected, setSelectedUnGroupId, setOrderBy, setOrder, setExpandedGroupIds]
  );

  const handleRowSelect = useCallback(
    (newSelected: any[]) => {
      setGroupSelected(newSelected);
    },
    [setGroupSelected]
  );

  const handleGroupRowSelect = useCallback(
    (newSelected: any[], groupId: string) => {
      setSelectedUnGroupId(groupId);
      setUnGroupSelected(newSelected);
    },
    [setUnGroupSelected, setSelectedUnGroupId]
  );

  const refetch = useCallback(() => {
    fetchNetworkElements(seeMore);
    if (isNodeView) {
      fetchPortsAndGroups();
      setPage(1);
    } else {
      fetchBandwidthPorts();
      setExpandedNeId("");
    }
  }, [isNodeView, fetchPortsAndGroups, fetchBandwidthPorts]);

  const { SaleDrawerComponent, handlePutForSaleOpen, handleSaleEdit, handleRemoveClick, removeLoadingId } = usePutUpForSaleBandwidth(
    isMounted,
    refetch,
    loading,
    setLoading
  );
  // const { GroupDrawer: GroupManagementComponent, handleGroupClick, handleEditGroupClick } = useGroup(
  //   expandedNeId,
  //   groupedSelected,
  //   { isBandwidth: true, allowAddToExisting: true },
  //   isMounted,
  //   refetch
  // );
  const { GroupDrawerProps } = useGroup(
    expandedNeId,
    { isMounted, isBandwidth: true, lowerDrawer: false },
    { groupedSelected, allowAddToExisting: true, refetch }
  );

  useEffect(() => {
    fetchNetworkElements(seeMore);
    refetch();
  }, [order, orderBy, page, total, isNodeView, order, searchTextNeElements, searchTextFeElements]);

  const handleCollapsibleGroupOpen = useCallback(
    (groupId: string) => {
      setExpandedGroupIds(Array.from(new Set([...expandedGroupIds, groupId])));
    },
    [setExpandedGroupIds, expandedGroupIds]
  );

  const handleCollapsibleGroupClose = useCallback(
    (groupId: string) => {
      const newIds = expandedGroupIds.filter((item) => item !== groupId);
      setExpandedGroupIds(Array.from(new Set([...newIds])));
    },
    [setExpandedGroupIds, expandedGroupIds]
  );

  const handleUnGroupClick = useCallback(() => {
    setUnGroupLoading(true);
    const payload = {
      groupId: selectedUnGroupId,
      nrIds: unGroupedSelected.filter((item) => item.nrId !== -1).map((item) => item.nrId),
      purchasePortIds: unGroupedSelected.filter((item) => item.purchasePortId !== -1).map((item) => item.purchasePortId),
    };
    ManageBandwidthService.unGroupNrs(payload)
      .then((response: any) => {
        if (!isMounted) return;
        toast.success(SuccessMessage.unGroupedSuccess, { icon: <img src={SuccessInfoIcon} alt="Success" /> });
        setUnGroupLoading(false);
        setGroupSelected([]);
        setUnGroupSelected([]);
        setSelectedUnGroupId("");
        fetchPortsAndGroups();
      })
      .catch(() => {
        if (!isMounted) return;
        toast.error(Errors.internalServerError, { icon: <img src={ErrorInfoIcon} alt="Error" className="error-icon" /> });
        setUnGroupLoading(false);
      });
  }, [setUnGroupLoading, isMounted, setGroupSelected, setUnGroupSelected, setSelectedUnGroupId, selectedUnGroupId, unGroupedSelected]);

  const handleSearchTextChanged = useCallback(
    (searchText: string) => {
      setSearchText(searchText);
      setPage(1);
    },
    [setSearchText, searchText, setPage]
  );
  const handleSearchChange = useCallback(
    (event) => {
      setSearchText(event.target.value);

      if (!event.target.value) {
        handleSearchTextChanged("");
        fetchBandwidthPorts();
      }
    },
    [setSearchText, handleSearchTextChanged, fetchBandwidthPorts]
  );
  const handleSearchClicked = useCallback(() => {
    handleSearchTextChanged(searchText.trim());
  }, [searchText, handleSearchTextChanged]);

  const handleKeypress = useCallback(
    (e) => {
      if (e.key === "Enter") {
        fetchBandwidthPorts();
      }
    },
    [fetchBandwidthPorts]
  );

  const [requestVerificationLoading, setRequestVerificationLoading] = useState(false);

  const handleRequestVerificationClick = useCallback(
    (port: any, prefix: string) => {
      setRequestVerificationLoading(true);
      const payload = {
        nrIds: [port[`${prefix}port_id`]],
      };
      ManagePortsService.setRequestVerification(payload)
        .then((response: any) => {
          if (!isMounted) return;
          toast.success(SuccessMessage.requestVerificationSuccessful, { icon: <img src={SuccessInfoIcon} alt="Success" /> });
          setGroupSelected([]);
          setUnGroupSelected([]);
          setSelectedUnGroupId("");
          fetchPortsAndGroups();
        })
        .catch(() => {
          if (!isMounted) return;
          toast.error(Errors.internalServerError, { icon: <img src={ErrorInfoIcon} alt="Error" className="error-icon" /> });
          setRequestVerificationLoading(false);
        });
      setRequestVerificationLoading(false);
    },
    [fetchPortsAndGroups, setSelectedUnGroupId, setGroupSelected, setUnGroupSelected, setUnGroupLoading, setGroupLoading, setRequestVerificationLoading]
  );

  const headCellsData: HeadCellType[] = [
    {
      id: "nne_name",
      align: "center",
      disablePadding: false,
      sortEnabled: true,
      label: "Near End Node",
      // searchEnabled: true,
      width: "10%",
    },
    {
      id: "fne_name",
      align: "center",
      disablePadding: false,
      sortEnabled: true,
      // searchEnabled: true,
      label: "Far End Node",
      width: "10%",
    },
    {
      id: "space",
      align: "left",
      disablePadding: false,
      sortEnabled: false,
      label: "",
      width: "2%",
    },
    {
      id: "bandwidth_description",
      align: "left",
      disablePadding: false,
      sortEnabled: false,
      label: "Link Description",
      width: "15%",
    },
    {
      id: "provider",
      align: "left",
      disablePadding: false,
      sortEnabled: true,
      label: "Provider",
      width: "15%",
    },
    // {
    //   id: 'farend_port_description',
    //   align: 'right',
    //   disablePadding: false,
    //   sortEnabled: false,
    //   label: 'Far End Port',
    //   width: '15%',
    // },
    {
      id: "link_status",
      align: "left",
      disablePadding: false,
      sortEnabled: false,
      label: "Link Status",
      width: "8%",
    },
    {
      id: "action",
      align: "left",
      disablePadding: false,
      sortEnabled: false,
      label: "Marketplace",
      width: "8%",
    },
  ];

  const handleNeInputChange = useCallback(
    (input: string) => {
      setNeInput(input);
    },
    [setNeInput]
  );
  const handleNeValueChange = useCallback(
    (value: string) => {
      setSearchTextNeElements(value);
    },
    [setSearchTextNeElements]
  );
  const handleFeValueChange = useCallback(
    (value: string) => {
      setSearchTextFeElements(value);
    },
    [setSearchTextFeElements]
  );
  const handleFeInputChange = useCallback(
    (input: string) => {
      setFeInput(input);
    },
    [setFeInput]
  );
  const handleSearchClose = useCallback(
    (id: any) => {
      if (id === "nne_name") {
        setSearchTextNeElements("");
        setNeInput("");
      } else {
        setSearchTextFeElements("");
        setFeInput("");
      }
    },
    [setSearchTextNeElements, setSearchTextFeElements, setFeInput, setNeInput]
  );

  const handleRequestSearch = useCallback(
    (e: any) => {
      if (e.target.name === "nne_name") {
        setPage(1);
        setSearchTextNeElements(e.target.value);
      } else {
        setPage(1);
        setSearchTextFeElements(e.target.value);
      }
    },
    [setSearchTextNeElements, setSearchTextFeElements]
  );

  const [editBandwidthOpen, setEditBandwidthOpen] = useState<boolean>(false);
  const [selectedRowForEdit, setSelectedRowForEdit] = useState<any>();
  const [portNameNEToEdit, setPortNameNEToEdit] = useState<string>("");
  const [portDescriptionNEToEdit, setPortDescriptionNEToEdit] = useState<string>("");
  const [portNameFEToEdit, setPortNameFEToEdit] = useState<string>("");
  const [portDescriptionFEToEdit, setPortDescriptionFEToEdit] = useState<string>("");
  const [bandwidthDescriptionToEdit, setBandwidthDescriptionToEdit] = useState<string>("");
  const [nearendError, setNearendError] = useState<string>("");
  const [farendError, setFarendError] = useState<string>("");
  const [editLoading, setEditLoading] = useState<boolean>(false);

  const handleEditBandwidth = useCallback(
    (bandwidthPort: any) => {
      setSelectedRowForEdit(bandwidthPort);
      setPortNameNEToEdit(bandwidthPort.nearend_port_name || bandwidthPort.port_name || "");
      setPortNameFEToEdit(bandwidthPort.farend_port_name || "");
      setPortDescriptionNEToEdit(bandwidthPort.nearend_port_description || bandwidthPort.port_description || "");
      setPortDescriptionFEToEdit(bandwidthPort.farend_port_description || "");
      setBandwidthDescriptionToEdit(bandwidthPort.bandwidth_description || "");
      setEditBandwidthOpen(true);
    },
    [
      setSelectedRowForEdit,
      setPortNameNEToEdit,
      setPortNameFEToEdit,
      setPortDescriptionNEToEdit,
      setPortDescriptionFEToEdit,
      setBandwidthDescriptionToEdit,
      setEditBandwidthOpen,
    ]
  );

  const handleDeleteOffNetBandwidth = useCallback(() => {
    const payload = {
      bandwidthId: selectedRowForEdit.bandwidth_detail_id,
    };

    ManageBandwidthService.deleteOffNetBandwidth(payload)
      .then((response: any) => {
        if (!isMounted) return;
        setEditBandwidthOpen(false);
        setBandwidthDescriptionToEdit("");
        setPortNameNEToEdit("");
        setPortDescriptionNEToEdit("");
        setPortNameFEToEdit("");
        setPortDescriptionFEToEdit("");
        setSelectedRowForEdit("");
        setNearendError("");
        setFarendError("");
        refetch();
        setTimeout(() => {
          if (!isMounted) return;
          toast.success(SuccessMessage.deleteBandwidth, { icon: <img src={SuccessInfoIcon} alt="Success" /> });
        });
      })
      .catch((e: any) => {
        toast.error("Bandwidth was not successfully deleted", { icon: <img src={ErrorInfoIcon} alt="Error" /> });
      });
  }, [selectedRowForEdit, refetch, isMounted]);

  const handleCloseEditBandwidth = useCallback(() => {
    setEditBandwidthOpen(false);
    setBandwidthDescriptionToEdit("");
    setPortNameNEToEdit("");
    setPortDescriptionNEToEdit("");
    setPortNameFEToEdit("");
    setPortDescriptionFEToEdit("");
    setSelectedRowForEdit("");
    setNearendError("");
    setFarendError("");
  }, [
    setEditBandwidthOpen,
    setNearendError,
    setFarendError,
    setBandwidthDescriptionToEdit,
    setSelectedRowForEdit,
    setPortNameNEToEdit,
    setPortDescriptionNEToEdit,
    setPortNameFEToEdit,
    setPortDescriptionFEToEdit,
  ]);
  const handleCancelSubscription = useCallback(
    (port: any) => {
      setCancelSubLoading(port.bandwidth_detail_id);
      const payload: any = {
        purchaseId: port.purchase_id,
        bandwidthDetailId: port.bandwidth_detail_id,
      };

      ManageBandwidthService.cancelSubscription(payload)
        .then((response: any) => {
          if (!isMounted) return;
          toast.success(SuccessMessage.cancelSubscription, { icon: <img src={SuccessInfoIcon} alt="Success" /> });
          fetchBandwidthPorts();
          setCancelSubLoading("");
          handleCloseEditBandwidth();
        })
        .catch((e: any) => {
          if (!isMounted) return;
          setCancelSubLoading("");
          if (e.errorCode === ErrorCodesMapping[1013]) {
            toast.error(ErrorCodeMessageMapping[1013], { icon: <img src={ErrorInfoIcon} alt="Error" className="error-icon" /> });
            fetchBandwidthPorts();
          } else {
            toast.error(Errors.internalServerError, { icon: <img src={ErrorInfoIcon} alt="Error" className="error-icon" /> });
          }
        });
    },
    [setCancelSubLoading, fetchBandwidthPorts]
  );

  const handleFieldChange = useCallback(
    (fieldName: string, value: string) => {
      setNearendError("");
      setFarendError("");
      if (fieldName === EditBandwidthFieldMapping.portNameNE) {
        setPortNameNEToEdit(value);
      }
      if (fieldName === EditBandwidthFieldMapping.portDescriptionNE) {
        setPortDescriptionNEToEdit(value);
      }
      if (fieldName === EditBandwidthFieldMapping.portNameFE) {
        setPortNameFEToEdit(value);
      }
      if (fieldName === EditBandwidthFieldMapping.portDescriptionFE) {
        setPortDescriptionFEToEdit(value);
      }
      if (fieldName === EditBandwidthFieldMapping.description) {
        setBandwidthDescriptionToEdit(value);
      }
    },
    [
      setPortNameNEToEdit,
      setNearendError,
      setFarendError,
      setPortDescriptionNEToEdit,
      setPortNameFEToEdit,
      setPortDescriptionFEToEdit,
      setBandwidthDescriptionToEdit,
    ]
  );

  if (loading) {
    rows = <LoadingRow colSpan={headCellsData.length + 1} />;
  } else if (total === 0) {
    rows = <EmptyRow text={TableConstants.noRecordsFound} colSpan={headCellsData.length + 1} />;
  } else {
    const bandwidthPortRows = bandwidthPorts.map((bandwidthPort: any, index) => {
      return (
        <TableData
          refetch={refetch}
          onEditBandwidth={handleEditBandwidth}
          onCancelSubscription={handleCancelSubscription}
          cancelSubLoading={cancelSubLoading}
          onSaleEdit={handleSaleEdit}
          onRemoveClick={handleRemoveClick}
          removeLoadingId={removeLoadingId}
          key={bandwidthPort.bandwidth_detail_id}
          handlePutForSaleOpen={() => handlePutForSaleOpen(bandwidthPort)}
          bandwidthPort={bandwidthPort}
        />
      );
    });
    rows = <TableBody>{bandwidthPortRows}</TableBody>;
  }

  const totalPages = Math.ceil(total / TableConstants.perPage);
  // const collapsibleContent = networkElements.map((ne: any, index: number) => {
  //   return <CollapsibleNe on_net={ne.on_net} onSaleClick={handlePutForSaleOpen} onSaleEdit={handleSaleEdit} onRemoveClick={handleRemoveClick} removeLoadingId={removeLoadingId} key={index} open={!!(expandedNeId == ne.id)} ports={portsInNe} groups={groupsInNe} loading={portsGroupsLoading} selectedUnGroupId={selectedUnGroupId} order={order} orderBy={orderBy} selected={groupedSelected} unGroupedSelected={unGroupedSelected} id={ne.id} text={ne.ne_name} expandedGroupIds={expandedGroupIds} onCollapsibleNEClose={handleCollapsibleNEClose} onCollapsibleNEOpen={handleCollapsibleNEOpen} onRequestSort={handleRequestSort} onSelectRow={handleRowSelect} onGroupRowSelect={handleGroupRowSelect} onCollapsibleGroupOpen={handleCollapsibleGroupOpen} onCollapsibleGroupClose={handleCollapsibleGroupClose} onEditGroupClick={handleEditGroupClick} />;
  // });
  const [collapsibleContent, setCollapsibleContent] = useState<any>();
  const updateCollapsibleContent = useCallback(() => {
    const CC = networkElements.map((ne: any, index: number) => {
      if (Number(expandedNeId) === Number(ne.id)) {
        return (
          <CollapsibleNe
            refetch={refetch}
            ne={ne}
            onEditBandwidth={handleEditBandwidth}
            on_net={ne.on_net}
            onSaleClick={handlePutForSaleOpen}
            onSaleEdit={handleSaleEdit}
            onRemoveClick={handleRemoveClick}
            removeLoadingId={removeLoadingId}
            key={index}
            open={!!(expandedNeId == ne.id)}
            ports={portsInNe}
            groups={groupsInNe}
            loading={portsGroupsLoading}
            selectedUnGroupId={selectedUnGroupId}
            order={order}
            orderBy={orderBy}
            selected={groupedSelected}
            unGroupedSelected={unGroupedSelected}
            id={ne.id}
            text={ne.ne_name}
            expandedGroupIds={expandedGroupIds}
            onCollapsibleNEClose={handleCollapsibleNEClose}
            onCollapsibleNEOpen={handleCollapsibleNEOpen}
            onRequestSort={handleRequestSort}
            onSelectRow={handleRowSelect}
            onGroupRowSelect={handleGroupRowSelect}
            onCollapsibleGroupOpen={handleCollapsibleGroupOpen}
            onCollapsibleGroupClose={handleCollapsibleGroupClose}
            onEditGroupClick={GroupDrawerProps.editGroup}
            handleUnGroupClick={handleUnGroupClick}
            handleGroupClick={GroupDrawerProps.addGroup}
            onRequestVerification={handleRequestVerificationClick}
            unGroupLoading={unGroupLoading}
            groupLoading={groupLoading}
          />
        );
      }
      return (
        <CollapsibleNe
          refetch={() => {}}
          ne={ne}
          onEditBandwidth={() => {}}
          open={false}
          on_net={ne.on_net}
          onSaleClick={() => {}}
          onSaleEdit={() => {}}
          onRemoveClick={() => {}}
          removeLoadingId=""
          key={index}
          ports={[]}
          groups={[]}
          loading={false}
          selectedUnGroupId=""
          order={order}
          orderBy=""
          selected={[]}
          unGroupedSelected={[]}
          id={ne.id}
          text={ne.ne_name}
          expandedGroupIds={[]}
          onCollapsibleNEClose={handleCollapsibleNEClose}
          onCollapsibleNEOpen={handleCollapsibleNEOpen}
          onRequestSort={() => {}}
          onSelectRow={() => {}}
          onGroupRowSelect={() => {}}
          onCollapsibleGroupOpen={() => {}}
          onCollapsibleGroupClose={() => {}}
          onEditGroupClick={() => {}}
          handleUnGroupClick={() => {}}
          handleGroupClick={() => {}}
          onRequestVerification={() => {}}
          unGroupLoading={false}
          groupLoading={false}
        />
      );
    });

    setCollapsibleContent([
      ...CC,
      <Button
        className="see-more-button"
        key={networkElements.length + 1 || 0}
        type="reset"
        variant="greyed"
        node={seeMore ? "See Less" : "See More"}
        onClick={() => {
          fetchNetworkElements(!seeMore);
        }}
      />,
    ]);
  }, [
    fetchNetworkElements,
    setNetworkElements,
    removeLoadingId,
    expandedNeId,
    portsInNe,
    groupsInNe,
    portsGroupsLoading,
    selectedUnGroupId,
    order,
    orderBy,
    groupedSelected,
    unGroupedSelected,
    expandedGroupIds,
    seeMore,
    handleCollapsibleNEClose,
    handleCollapsibleNEOpen,
    handleRequestSort,
    handleRowSelect,
    handleGroupRowSelect,
    handleCollapsibleGroupOpen,
    handleCollapsibleGroupClose,
    GroupDrawerProps.editGroup,
    networkElements,
    expandedNeId,
    handleCancelSubscription,
    handlePutForSaleOpen,
    handleSaleEdit,
    handleSaleEdit,
    cancelSubLoading,
  ]);

  useEffect(() => {
    updateCollapsibleContent();
  }, [
    fetchNetworkElements,
    setNetworkElements,
    removeLoadingId,
    expandedNeId,
    portsInNe,
    groupsInNe,
    portsGroupsLoading,
    selectedUnGroupId,
    order,
    orderBy,
    groupedSelected,
    unGroupedSelected,
    expandedGroupIds,
    handleCollapsibleNEClose,
    handleCollapsibleNEOpen,
    handleRequestSort,
    handleRowSelect,
    handleGroupRowSelect,
    handleCollapsibleGroupOpen,
    handleCollapsibleGroupClose,
    GroupDrawerProps.editGroup,
    networkElements,
    expandedNeId,
    handleCancelSubscription,
    handlePutForSaleOpen,
    handleSaleEdit,
    handleSaleEdit,
    cancelSubLoading,
  ]);

  useEffect(() => {
    if (networkElementContext && networkElementContext.id) {
      handleCollapsibleNEOpen(networkElementContext.id);
      window.history.replaceState({}, document.title);
    }
  }, [networkElements]);

  const getAddBandwidthState = useCallback(() => {
    if (isNodeView && expandedNeId) {
      return {
        nearendNe: networkElements.find((ele) => ele.id == expandedNeId),
        farendNe: null,
      };
    }
    return {
      nearendNe: networkElements.find((ele) => ele.ne_name === searchTextNeElements),
      farendNe: networkElements.find((ele) => ele.ne_name === searchTextFeElements),
    };
  }, [searchTextFeElements, searchTextNeElements, networkElements, expandedNeId, isNodeView]);

  return (
    <>
      {!isNodeView ? (
        <div className="verification-root manage-link-view" data-testid="admin-verification">
          <div className="sticky-top">
            <div className="title-main">
              <div className="title-root">
                <h3>Manage Links</h3>
              </div>
            </div>
            <div className="title-sub">
              {/* Dropdown */}
              <div className="flex">
                <div className="dropdown-filters">
                  <Dropdown
                    title="Select Near End"
                    className="bandwidth filter"
                    inputValue={neInput}
                    onInputChange={handleNeInputChange}
                    value={searchTextNeElements}
                    onNetNames={allNetworkElements.filter((item: any) => item.on_net).map((item: any) => item.ne_name)}
                    options={allNetworkElements.map((item: any) => item.ne_name).filter((item: any) => item !== searchTextFeElements)}
                    onChange={handleNeValueChange}
                  />
                  <img src={SwipeIcon} alt="connector" />
                  <Dropdown
                    title="Select Far End"
                    className="bandwidth filter"
                    inputValue={feInput}
                    onInputChange={handleFeInputChange}
                    value={searchTextFeElements}
                    onNetNames={allNetworkElements.filter((item: any) => item.on_net).map((item: any) => item.ne_name)}
                    options={allNetworkElements.map((item: any) => item.ne_name).filter((item: any) => item !== searchTextNeElements)}
                    onChange={handleFeValueChange}
                  />
                </div>
                {/* Search bar */}
                <div className="search-box">
                  <div style={{ minWidth: "300px" }} className="group-ungroup-div table-filters ">
                    <div className="group-ungroup-div table-filters">
                      <Input
                        variant="input-pre-icon"
                        name="user-search"
                        placeholder="Search"
                        icon={SearchIcon}
                        onChange={handleSearchChange}
                        onKeypress={handleKeypress}
                        onBlur={() => {
                          if (searchText !== prevSearchText) fetchBandwidthPorts();
                        }}
                      />
                      {/* <div className="search-button-div" style={{ paddingLeft: "3px" }}>
                      <Button
                        variant="search"
                        node="Search"
                        className="search-btn"
                        disabled={searchText === prevSearchText}
                        onClick={() => fetchBandwidthPorts()}
                      />
                    </div> */}
                    </div>
                  </div>
                </div>
              </div>
              <div className="group-ungroup-div table-filters">
                <Button
                  variant="grouped"
                  node="Add Link"
                  className="verify-all-btn"
                  onClick={() => navigate(AuthRoutes.addBandwidth, { state: getAddBandwidthState() })}
                />
                <div className="navigation">
                  <img src={ListViewSelected} alt="List View" />
                  <img src={NodeView} style={{ cursor: "pointer" }} onClick={() => handlePortViewChange(true)} alt="Port View" />
                </div>
              </div>
            </div>
          </div>
          <div className="sticky-bottom">
            <BandwidthTable
              bandwidthPorts={bandwidthPorts}
              onRequestSort={handleRequestSort}
              onRequestSearch={handleRequestSearch}
              onSearchClose={handleSearchClose}
              rows={rows}
              totalPages={totalPages}
              selected={selected}
              headCellsData={headCellsData}
              page={page}
              order={order}
              orderBy={orderBy}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
      ) : (
        <div className="verification-root manage-link-view" data-testid="admin-verification">
          <div className="sticky-top">
            <div className="title-main">
              <div className="title-root">
                <h3>Manage Links</h3>
              </div>
            </div>
            <div className="title-sub">
              <div className="dropdown-filters">
                <Dropdown
                  title="Select Node"
                  className="bandwidth filter"
                  inputValue={neInput}
                  onInputChange={handleNeInputChange}
                  value={searchTextNeElements}
                  onNetNames={networkElements.filter((item: any) => item.on_net).map((item: any) => item.ne_name)}
                  options={networkElements.map((item: any) => item.ne_name).filter((item: any) => item !== searchTextFeElements)}
                  onChange={handleNeValueChange}
                />
              </div>
              <div>
                <div className="group-ungroup-div">
                  <Button
                    variant="grouped"
                    node="Add Link"
                    className="verify-all-btn"
                    onClick={() => navigate(AuthRoutes.addBandwidth, { state: getAddBandwidthState() })}
                  />
                  ;
                  <div className="navigation">
                    <img src={ListView} alt="List View" onClick={() => handlePortViewChange(false)} style={{ cursor: "pointer" }} />
                    <img src={NodeViewSelected} alt="Port View" />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="sticky-bottom">{collapsibleContent}</div>
        </div>
      )}
      {GroupDrawerProps.drawerComponent}
      {SaleDrawerComponent}
      <EditBandwidthDrawer
        portView={isNodeView}
        submitEdits={() => {}}
        deleteOffNetBandwidth={handleDeleteOffNetBandwidth}
        bandwidth={selectedRowForEdit}
        nearend_name={portNameNEToEdit}
        farend_name={portNameFEToEdit}
        nearend_description={portDescriptionNEToEdit}
        farend_description={portDescriptionFEToEdit}
        description={bandwidthDescriptionToEdit}
        loading={editLoading}
        neError={nearendError}
        feError={farendError}
        open={editBandwidthOpen}
        onClose={handleCloseEditBandwidth}
        onFieldChange={handleFieldChange}
        handleCancelSubscription={handleCancelSubscription}
        cancelLoading={!!cancelSubLoading}
      />
    </>
  );
};

export default ManageBandwidth;
